import _, {ceil, chain, range, times} from 'lodash';
import memoize from 'lru-memoize';
import {DateTime} from 'luxon';
import React, {FC} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Dropdown, DropdownProps} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownList} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {DateTimeFormatterBase} from '../../../../srcCommon/formatters/dateTime/dateTimeFormatterBase';
import {FocusModesEnum} from '../../../../srcCommon/scroll/scrollerTypes';
import {
  areTimesEqual,
  build12Or24hFullOptionsForFormat,
  TimeFormatsEnum,
} from '../../../helpers/dateTimeHelpers';
import {VariableHeightScroller} from '../scroll/variableHeightScroller';

// Only allow 10-15-30 minute increments
export type IncrementMinutesValues = 10 | 15 | 30;

/*
 * Constants.
 */

const getTimeValues = memoize()(
  (
    minTime: DateTime | undefined,
    maxTime: DateTime | undefined,
    incrementMinutes: IncrementMinutesValues,
  ) => {
    const localDate = DateTime.local();
    return chain(range(24))
      .map((hour) => {
        const hourDate = localDate.startOf('hour').set({hour});
        const hourDates = times(ceil(60 / incrementMinutes), (i) =>
          hourDate.set({minute: incrementMinutes * i}),
        );

        return hourDates.filter((d) => (!minTime || minTime < d) && (!maxTime || maxTime > d));
      })
      .flatten()
      .value();
  },
);

/*
 * Props.
 */

interface TimeDropdownProps extends DropdownProps {
  timeFormat: TimeFormatsEnum;
  selectedTime?: DateTime;
  onTimeSelect?: HandlerOf<DateTime | undefined>;
  minTime?: DateTime;
  maxTime?: DateTime;
  incrementMinutes?: IncrementMinutesValues;
  emptyValue?: string;
  width?: number;
}

/*
 * Style.
 */

const StyledDropdownScroller = styled(VariableHeightScroller)`
  margin: -7px 0;
  padding: 7px 0;
`;

/*
 * Component.
 */

export const TimeDropdown: FC<TimeDropdownProps> = (props) => {
  const {selectedTime} = props;
  const focusKey = selectedTime && makeTimeKey(selectedTime);
  const width = props.width || (props.timeFormat === TimeFormatsEnum.H12 ? 120 : 100);
  return (
    <Dropdown {...props} width={width} maxHeight={200}>
      <DropdownList {...props}>
        <StyledDropdownScroller focusKey={focusKey} focusMode={FocusModesEnum.MIDDLE}>
          {renderItems(props)}
        </StyledDropdownScroller>
      </DropdownList>
    </Dropdown>
  );
};

function renderItems(props: TimeDropdownProps) {
  const {minTime, maxTime, selectedTime, timeFormat, onTimeSelect, emptyValue} = props;
  // Default time increments to 30 minutes if no incrementMinutes prop is passed
  const incrementMinutes = props.incrementMinutes || 30;
  const format = build12Or24hFullOptionsForFormat(timeFormat);

  return _(getTimeValues(minTime, maxTime, incrementMinutes))
    .concat(props.selectedTime ? [props.selectedTime] : [])
    .compact()
    .uniqBy((r) => makeTimeKey(r))
    .sortBy((r) => r.toMillis())
    .map((time) => {
      const isChecked = areTimesEqual(time, selectedTime);
      return (
        <DropdownItem
          key={makeTimeKey(time)}
          isChecked={isChecked}
          onSelect={() => onTimeSelect && onTimeSelect(time)}
        >
          <DropdownItemContent isChecked={isChecked}>
            <DateTimeFormatterBase value={time.toJSDate()} format={format} />
          </DropdownItemContent>
        </DropdownItem>
      );
    })
    .unshift(
      // @ts-expect-error the types of `unshift` don't allow an `undefined` value, which makes sense but we run `compact()` just after so it should be safe
      emptyValue ? (
        <DropdownItem key="empty-value" isChecked={!selectedTime} onSelect={() => onTimeSelect?.(undefined)}>
          <DropdownItemContent isChecked={!selectedTime}>{props.emptyValue}</DropdownItemContent>
        </DropdownItem>
      ) : undefined,
    )
    .compact()
    .value();
}

function makeTimeKey(time: DateTime) {
  return `${time.hour}-${time.minute}`;
}
